import React, { Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { Brain, BatteryWarning, Activity, Dna, Zap, Quote, CheckCircle2, ShieldCheck, ArrowRight, Star, Clock, BookOpen, Coffee, Plane, ListChecks, Moon, GraduationCap, Microscope, FlaskConical, Award, Building2, BookMarked } from 'lucide-react';
import { CountdownBar } from '../components/CountdownBar';
import { CTAButton } from '../components/CTAButton';
import { FAQAccordion } from '../components/FAQAccordion';
import { Footer } from '../components/Footer';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function LandingPage() {
  const faqs = [{
    question: '¿Cuánto tiempo requiere?',
    answer: 'Solo 15 minutos de cambios estratégicos al día.'
  }, {
    question: '¿Necesito aparatos caros?',
    answer: 'No. El protocolo se basa en biología, no en gadgets.'
  }, {
    question: '¿Es seguro para mí?',
    answer: 'Sí, se basa en optimización de hábitos naturales respaldados por ciencia.'
  }];
  return <div className="min-h-screen bg-marble text-slate-900 selection:bg-emerald/20" data-id="element-95">
      <CountdownBar data-id="element-96" />

      {/* BRAND HEADER */}
      <header className="w-full border-b border-slate-200 bg-white/90 backdrop-blur-md sticky top-[48px] md:top-[52px] z-40" data-id="element-97">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-center md:justify-start" data-id="element-98">
          <img src="https://cdn.magicpatterns.com/uploads/exH9G2rgUuiMQFMLYuFBer/Captura_de_pantalla_2026-06-11_a_la(s)_11.21.26_a.m..png?w=400&q=85" alt="MinHealthGroup" width={200} height={50} decoding="async" className="h-8 md:h-10 w-auto object-contain mix-blend-multiply" data-id="element-99" />
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative px-4 py-24 md:py-36 overflow-hidden" data-id="element-100">
        {/* Background image */}
        <img src="https://cdn.magicpatterns.com/uploads/4oGFRrtQyDNKmuScsTTvJ1/1.jpg?w=1600&q=75" alt="Ejecutivo con reloj de lujo — recupera tu ventaja competitiva" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" data-id="element-101" />
        {/* Dark emerald overlay so the white text pops */}
        <div className="absolute inset-0 bg-emerald-dark/70 md:bg-gradient-to-r md:from-emerald-dark/90 md:via-emerald-dark/75 md:to-emerald-dark/50" aria-hidden="true" data-id="element-102"></div>

        <div className="relative max-w-6xl mx-auto" data-id="element-103">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="text-left max-w-2xl" data-id="element-104">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 font-serif drop-shadow-sm" data-id="element-105">
              Recupera tu Ventaja Competitiva:{' '}
              <span className="text-gold italic" data-id="element-106">
                El Protocolo de Longevidad Celular para Líderes 45+
              </span>
            </h1>
            <p className="text-lg md:text-xl text-marble/90 mb-8 leading-relaxed" data-id="element-107">
              Deja de gestionar tu agotamiento y empieza a revertir tu edad
              biográfica. Un sistema de biohacking de grado científico diseñado
              para la vida ejecutiva real. Sin protocolos imposibles, solo
              resultados medibles.
            </p>
            <div className="flex flex-col items-start gap-3" data-id="element-108">
              <CTAButton data-id="element-109" />
              <p className="text-sm text-marble/70 font-medium ml-2" data-id="element-110">
                Inversión única de $27 — Acceso inmediato
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AUTHORITY SECTION */}
      <section className="py-16 px-4 bg-white border-y border-slate-200" data-id="element-111">
        <div className="max-w-6xl mx-auto text-center" data-id="element-112">
          <p className="text-xs md:text-sm text-slate-500 font-semibold uppercase tracking-[0.2em] mb-3" data-id="element-113">
            Fundamento Científico
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-emerald font-serif mb-12 max-w-2xl mx-auto" data-id="element-114">
            Basado en los principios de longevidad validados por las
            instituciones de salud más prestigiosas del mundo.
          </h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200 rounded-2xl overflow-hidden border border-slate-200 shadow-soft" data-id="element-115">
            {[{
            icon: GraduationCap,
            name: 'Harvard Medical School'
          }, {
            icon: Award,
            name: 'Stanford Medicine'
          }, {
            icon: Microscope,
            name: 'MIT'
          }].map(inst => {
            const Icon = inst.icon;
            return <motion.div key={inst.name} variants={fadeIn} className="group bg-white flex flex-col items-center justify-center gap-3 px-4 py-8 transition-colors hover:bg-slate-50" data-id="element-116">
                  <Icon className="w-7 h-7 text-slate-400 group-hover:text-gold transition-colors" data-id="element-117" />
                  <span className="text-sm md:text-base font-serif text-slate-500 group-hover:text-emerald transition-colors leading-tight" data-id="element-118">
                    {inst.name}
                  </span>
                </motion.div>;
          })}
          </motion.div>

          <p className="text-xs text-slate-400 mt-8 max-w-2xl mx-auto leading-relaxed" data-id="element-119">
            El Protocolo 45+ cita y se apoya en estudios publicados por estas
            instituciones y revistas científicas. Los nombres se muestran
            únicamente con fines de referencia editorial; MinHealthGroup no está
            afiliado ni respaldado oficialmente por estas entidades.
          </p>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-24 px-4 bg-marble" data-id="element-120">
        <div className="max-w-3xl mx-auto text-center" data-id="element-121">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-122">
            <h2 className="text-3xl md:text-4xl font-bold text-emerald mb-8 font-serif" data-id="element-123">
              ¿Estás pagando el "Impuesto Biológico" del éxito?
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-124">
              Neblina mental a las 3:00 PM. Fatiga que el café ya no cura.
              Recuperación lenta tras un entrenamiento o una jornada larga. No
              estás envejeciendo, estás{' '}
              <strong className="text-walnut" data-id="element-125">"desoptimizado"</strong>.
            </p>
            <div className="p-6 bg-white border border-slate-200 rounded-xl shadow-soft mt-8" data-id="element-126">
              <p className="text-emerald font-medium text-lg italic" data-id="element-127">
                El Protocolo 45+ es la reingeniería que tus células necesitan
                para volver a rendir como hace dos décadas.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-24 px-4 bg-emerald text-marble border-y border-emerald-light" data-id="element-128">
        <div className="max-w-4xl mx-auto" data-id="element-129">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-130">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-serif" data-id="element-131">
              Optimización Celular sin Protocolos Extremos.
            </h2>
            <p className="text-lg text-marble/80 max-w-2xl mx-auto" data-id="element-132">
              Olvida las 100 pastillas diarias o las rutinas de 2 horas.
              MinHealthGroup ha destilado la ciencia de la longevidad en un
              sistema ejecutable de{' '}
              <strong className="text-gold" data-id="element-133">15 minutos al día</strong>.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8" data-id="element-134">
            <motion.div variants={fadeIn} className="bg-emerald-dark/50 p-8 rounded-2xl border border-emerald-light" data-id="element-135">
              <Dna className="w-10 h-10 text-gold mb-6" data-id="element-136" />
              <h3 className="text-xl font-bold text-white mb-3 font-serif" data-id="element-137">
                Activación de Sirtuinas
              </h3>
              <p className="text-marble/70" data-id="element-138">
                Reactiva tus genes de longevidad.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-emerald-dark/50 p-8 rounded-2xl border border-emerald-light" data-id="element-139">
              <Zap className="w-10 h-10 text-gold mb-6" data-id="element-140" />
              <h3 className="text-xl font-bold text-white mb-3 font-serif" data-id="element-141">
                Optimización Mitocondrial
              </h3>
              <p className="text-marble/70" data-id="element-142">
                Recupera el enfoque mental de tus 30s.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-emerald-dark/50 p-8 rounded-2xl border border-emerald-light" data-id="element-143">
              <Activity className="w-10 h-10 text-gold mb-6" data-id="element-144" />
              <h3 className="text-xl font-bold text-white mb-3 font-serif" data-id="element-145">
                Reset Epigenético
              </h3>
              <p className="text-marble/70" data-id="element-146">
                Hackea tu reloj biológico desde la raíz.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* VALUE STACK SECTION */}
      <section className="py-24 px-4 bg-marble" data-id="element-147">
        <div className="max-w-5xl mx-auto" data-id="element-148">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-149">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-150">
              <motion.div variants={fadeIn} data-id="element-151">
                <img src="https://cdn.magicpatterns.com/uploads/o56cJhL5fL7rHfP3dG3zT8/2.jpg?w=900&q=75" alt="Optimización celular y genética — Protocolo de Longevidad 45+" loading="lazy" decoding="async" className="w-full rounded-2xl shadow-soft-lg ring-1 ring-slate-200" data-id="element-152" />
              </motion.div>
              <motion.div variants={fadeIn} data-id="element-153">
                <h2 className="text-3xl font-bold text-emerald mb-8 font-serif" data-id="element-154">
                  Lo que recibes hoy:
                </h2>
                <div className="space-y-4" data-id="element-155">
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex gap-4" data-id="element-156">
                    <BookOpen className="w-6 h-6 text-gold shrink-0 mt-1" data-id="element-157" />
                    <div data-id="element-158">
                      <h4 className="font-bold text-emerald" data-id="element-159">
                        Ebook Protocolo Longevidad 45+{' '}
                        <span className="text-slate-400 font-normal text-sm ml-2" data-id="element-160">
                          (Valor: $97)
                        </span>
                      </h4>
                      <p className="text-slate-600 text-sm mt-1" data-id="element-161">
                        El mapa maestro para la reversión biológica.
                      </p>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex gap-4" data-id="element-162">
                    <Moon className="w-6 h-6 text-gold shrink-0 mt-1" data-id="element-163" />
                    <div data-id="element-164">
                      <h4 className="font-bold text-emerald" data-id="element-165">
                        BONUS 1: The Executive Sleep Matrix{' '}
                        <span className="text-slate-400 font-normal text-sm ml-2" data-id="element-166">
                          (Valor: $67 - GRATIS)
                        </span>
                      </h4>
                      <p className="text-slate-600 text-sm mt-1" data-id="element-167">
                        La ciencia de pasar de "dormir" a "reparar". Cómo
                        alcanzar el sueño profundo que resetea tu cerebro cada
                        noche.
                      </p>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex gap-4" data-id="element-168">
                    <Activity className="w-6 h-6 text-gold shrink-0 mt-1" data-id="element-169" />
                    <div data-id="element-170">
                      <h4 className="font-bold text-emerald" data-id="element-171">
                        BONUS 2: Metabolic Precision Plan{' '}
                        <span className="text-slate-400 font-normal text-sm ml-2" data-id="element-172">
                          (Valor: $47 - GRATIS)
                        </span>
                      </h4>
                      <p className="text-slate-600 text-sm mt-1" data-id="element-173">
                        Combustible de alto octanaje. Qué comer y cuándo hacerlo
                        para mantener niveles de glucosa estables y energía
                        ilimitada.
                      </p>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex gap-4" data-id="element-174">
                    <Plane className="w-6 h-6 text-gold shrink-0 mt-1" data-id="element-175" />
                    <div data-id="element-176">
                      <h4 className="font-bold text-emerald" data-id="element-177">
                        BONUS 3: The Travel Biohacker{' '}
                        <span className="text-slate-400 font-normal text-sm ml-2" data-id="element-178">
                          (Valor: $37 - GRATIS)
                        </span>
                      </h4>
                      <p className="text-slate-600 text-sm mt-1" data-id="element-179">
                        Domina el Jet Lag y mantén el rendimiento máximo en
                        cualquier zona horaria.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.div variants={fadeIn} className="bg-white border border-gold/30 rounded-3xl p-10 text-center shadow-soft-lg max-w-3xl mx-auto" data-id="element-180">
              <p className="text-lg text-slate-500 font-medium" data-id="element-181">
                Valor Total del Sistema:{' '}
                <span className="line-through" data-id="element-182">$197.00</span>
              </p>
              <p className="text-3xl md:text-4xl font-bold text-emerald mt-2 font-serif" data-id="element-183">
                Tu inversión hoy: <span className="text-gold" data-id="element-184">$27.00</span>
              </p>
              <p className="text-base text-walnut font-semibold mt-4 mb-8" data-id="element-185">
                Estás ahorrando un 85% solo durante la fase de lanzamiento.
              </p>
              <div className="flex flex-col items-center gap-3" data-id="element-186">
                <CTAButton data-id="element-187" />
                <p className="text-sm text-slate-500 font-medium" data-id="element-188">
                  Inversión única de $27 — Acceso inmediato
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* GUARANTEE SECTION */}
      <section className="py-24 px-4 bg-walnut text-marble border-y border-walnut-light" data-id="element-189">
        <div className="max-w-3xl mx-auto" data-id="element-190">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center" data-id="element-191">
            <ShieldCheck className="w-20 h-20 text-gold mx-auto mb-6" data-id="element-192" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-serif" data-id="element-193">
              Garantía MinHealth 30 Días.
            </h2>
            <p className="text-lg text-marble/90 leading-relaxed mb-8" data-id="element-194">
              Estamos tan seguros de nuestra ciencia que si en 30 días no
              sientes una mejora notable en tu claridad mental y niveles de
              energía, te devolvemos el 100% de tu inversión. Sin preguntas. El
              riesgo es nuestro, la vitalidad es tuya.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 px-4 bg-slate-50 border-y border-slate-200" data-id="element-195">
        <div className="max-w-4xl mx-auto" data-id="element-196">
          <div className="text-center mb-16" data-id="element-197">
            <h2 className="text-3xl md:text-4xl font-bold text-emerald mb-4 font-serif" data-id="element-198">
              Preguntas Frecuentes
            </h2>
          </div>
          <FAQAccordion faqs={faqs} data-id="element-199" />
        </div>
      </section>

      <Footer data-id="element-200" />
    </div>;
}